import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  IconButton,
  Checkbox,
  TextField,
  Alert,
  ListItem,
  List,
  FormControlLabel,
  Switch,
  CircularProgress,
  Fade,
  Tooltip,
  Chip,
  Divider,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  SelectChangeEvent,
  useMediaQuery,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  InfoOutlined as InfoOutlinedIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  DriveFileMove as MoveIcon,
  DragIndicator as DragIndicatorIcon,
  ChevronRight as ChevronRightIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { ChecklistItem, ChecklistType, PasoTags, PriorityLevel } from '../types/api';
import { datedChecklistService } from '../services/api';
import ColorSwatchModal from './ColorSwatchModal';
import GenericModal from './GenericModal';
import MarkdownText from './MarkdownText';
import WeekdaySelector from './WeekdaySelector';
import MonthDayInput from './MonthDayInput';
import RecurrenceTypeSelector from './RecurrenceTypeSelector';
import RecurringItemBadge from './RecurringItemBadge';
import { DueDateChipLabel, NullSelectSymbol, PriorityChipLabel, PriorityMenuOption, PrioritySelectValue, TimeChipLabel } from './PasoTagVisuals';
import { AIRMASK_GREEN } from '../constants/colors';
import { getCurrentDayOfWeek } from '../utils/recurringUtils';
import { useRecurringItems } from '../contexts/RecurringItemsContext';
import { buildGroupedTagModel, flattenGroupedTagModel } from '../utils/tagGrouping';
import UpcomingDateEntryField from './UpcomingDateEntryField';
import SaveActionButton from './SaveActionButton';
import { buildUpcomingDateValue, emptyUpcomingDateErrors, parseUpcomingDateInput, validateUpcomingDateParts } from '../utils/upcomingDate';
import ContextMenu, { ContextMenuPosition } from './ContextMenu';

interface DateChecklistExpandProps {
  title: string;
  dayOfWeek?: string; // Day of the week: "SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"
  checklistType: ChecklistType;
  items: ChecklistItem[];
  itemCount?: number; // Item count (may be set before items are loaded)
  expanded: boolean;
  onExpandChange: (expanded: boolean) => void;
  onAddItem: (text: string, options?: { successMessage?: string | null }) => Promise<boolean | void>;
  onDeleteItem: (itemId: string) => void;
  onEditItem: (itemId: string, newText: string, options?: { tags?: PasoTags | null }) => void;
  onConvertToRecurring: (itemId: string, recurringMetadataId: string) => void;
  onConvertToNonRecurring: (itemId: string, newText: string) => void;
  onReloadAfterRecurringUpdate: (itemId: string) => void;
  onColorChange: (itemId: string, color: string) => void;
  readOnly?: boolean;
  reviewed?: boolean;
  onMarkReviewed?: (reviewed: boolean) => void;
  date: string; // Date of the checklist (for creating recurring items)
  showDateField?: boolean;
  onMoveItemToDate?: (itemId: string, targetDate: string, newText: string, options?: { tags?: PasoTags | null }) => Promise<void> | void;
  onToggleItemHidden?: (itemId: string, isHidden: boolean) => Promise<void> | void;
  onAddItemToPaso?: (itemId: string, targetChecklistType: ChecklistType) => Promise<void> | void;
  pasoChecklistConfigs?: ReadonlyArray<{ type: ChecklistType; title: string }>;
  isDragInProgress?: boolean;
}

const DateChecklistExpand: React.FC<DateChecklistExpandProps> = ({
  title,
  dayOfWeek,
  checklistType,
  items,
  itemCount,
  expanded,
  onExpandChange,
  onAddItem,
  onDeleteItem,
  onEditItem,
  onConvertToRecurring,
  onConvertToNonRecurring,
  onReloadAfterRecurringUpdate,
  onColorChange,
  readOnly = false,
  reviewed,
  onMarkReviewed,
  date,
  showDateField: showDateFieldOverride = false,
  onMoveItemToDate,
  onToggleItemHidden,
  onAddItemToPaso,
  pasoChecklistConfigs,
  isDragInProgress = false,
}) => {
  const { getRecurringItemAsync, updateRecurringItem } = useRecurringItems();

  const currentYear = new Date().getFullYear();
  
  const [newItemText, setNewItemText] = useState('');
  const [showAddInput, setShowAddInput] = useState(false);
  const [isMultiAddMode, setIsMultiAddMode] = useState(false);
  const [showMultiAddHint, setShowMultiAddHint] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [colorModalAnchor, setColorModalAnchor] = useState<HTMLElement | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedItemColor, setSelectedItemColor] = useState<string | undefined>(undefined);
  const [showHiddenItems, setShowHiddenItems] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [touchHoldItemId, setTouchHoldItemId] = useState<string | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<ContextMenuPosition | null>(null);
  const [contextMenuItemId, setContextMenuItemId] = useState<string | null>(null);
  const [moveSubmenuAnchor, setMoveSubmenuAnchor] = useState<HTMLElement | null>(null);
  const [expandedItemIds, setExpandedItemIds] = useState<Set<string>>(new Set());
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());
  const [isRecurring, setIsRecurring] = useState(false);
  const [addTagsEnabled, setAddTagsEnabled] = useState(false);
  const [tagCategoriesInput, setTagCategoriesInput] = useState('');
  const [isTagCategoriesFocused, setIsTagCategoriesFocused] = useState(false);
  const [dueDateValue, setDueDateValue] = useState('');
  const [isHuge, setIsHuge] = useState(false);
  const [estimatedTimeValue, setEstimatedTimeValue] = useState('');
  const [estimatedTimeUnit, setEstimatedTimeUnit] = useState<'' | 'minutes' | 'hours'>('minutes');
  const [opportunityCostIfNotDone, setOpportunityCostIfNotDone] = useState<'' | 'high' | 'moderate' | 'low'>('');
  const [valueIfDone, setValueIfDone] = useState<'' | 'high' | 'moderate' | 'low'>('');
  const [editDateParts, setEditDateParts] = useState(() => parseUpcomingDateInput(date));
  const [editDateErrors, setEditDateErrors] = useState(() => emptyUpcomingDateErrors());
  const [showRecurringDayReadOnlyTooltip, setShowRecurringDayReadOnlyTooltip] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState<'weekly' | 'monthly'>('weekly');
  const [recurringDays, setRecurringDays] = useState<number[]>([getCurrentDayOfWeek()]);
  const [monthDays, setMonthDays] = useState<number[]>([1]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const editFormRef = useRef<HTMLDivElement>(null);
  const addFormRef = useRef<HTMLDivElement>(null);
  const recurringDayTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressNextItemClickRef = useRef(false);
  const isTouchDevice = useMediaQuery('(hover: none) and (pointer: coarse)');
  const currentEditingItem = editingItemId ? items.find(item => item.id === editingItemId) : null;
  const showDateField = showDateFieldOverride && !readOnly;
  const supportsTagEditing = false;
  const supportsItemContextActions = !readOnly && Boolean(onToggleItemHidden) && Boolean(onAddItemToPaso) && Boolean(pasoChecklistConfigs?.length);
  const supportsReorderMode = supportsItemContextActions && isTouchDevice;
  const isContextMenuEnabled = supportsItemContextActions && (!supportsReorderMode || !isReorderMode);
  const isDraggingEnabled = !readOnly && (!supportsReorderMode || isReorderMode);

  const hideRecurringDayReadOnlyTooltip = () => {
    if (recurringDayTooltipTimeoutRef.current) {
      clearTimeout(recurringDayTooltipTimeoutRef.current);
      recurringDayTooltipTimeoutRef.current = null;
    }
    setShowRecurringDayReadOnlyTooltip(false);
  };

  const handleRecurringDayFieldInteraction = () => {
    if (!showDateField || !currentEditingItem?.recurringMetadataId) {
      return;
    }

    setShowRecurringDayReadOnlyTooltip(true);
    if (recurringDayTooltipTimeoutRef.current) {
      clearTimeout(recurringDayTooltipTimeoutRef.current);
    }
    recurringDayTooltipTimeoutRef.current = setTimeout(() => {
      setShowRecurringDayReadOnlyTooltip(false);
      recurringDayTooltipTimeoutRef.current = null;
    }, 2200);
  };

  useEffect(() => {
    return () => {
      if (recurringDayTooltipTimeoutRef.current) {
        clearTimeout(recurringDayTooltipTimeoutRef.current);
      }
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!contextMenuPosition) {
      setMoveSubmenuAnchor(null);
    }
  }, [contextMenuPosition]);

  // Track when items are being loaded after expansion
  useEffect(() => {
    if (expanded && items.length === 0 && itemCount !== undefined && itemCount > 0) {
      setIsLoadingItems(true);
    } else {
      setIsLoadingItems(false);
    }
  }, [expanded, items.length, itemCount]);

  const handleAddItem = async () => {
    if (newItemText.trim()) {
      const itemsToAdd = isMultiAddMode
        ? newItemText
          .split(/\n\s*\n+/)
          .map(text => text.trim())
          .filter(Boolean)
        : [newItemText.trim()];

      for (let index = 0; index < itemsToAdd.length; index += 1) {
        await onAddItem(itemsToAdd[index], {
          successMessage: index === itemsToAdd.length - 1
            ? itemsToAdd.length > 1
              ? 'Items added successfully'
              : 'Item added successfully'
            : null,
        });
      }

      setNewItemText('');
      setShowAddInput(false);
      setIsMultiAddMode(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (isTouchDevice) {
        return;
      }

      if (!e.shiftKey) {
        // Plain Enter saves the item
        e.preventDefault();
        handleAddItem();
      }
      // Shift+Enter: allow default behavior (insert newline)
    } else if (e.key === 'Escape') {
      setNewItemText('');
      setShowAddInput(false);
      setIsMultiAddMode(false);
    }
  };

  const handleAddButtonHover = (event: React.MouseEvent<HTMLElement>) => {
    setShowMultiAddHint(event.metaKey || event.ctrlKey);
  };

  const handleAddTextFieldBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!isTouchDevice) {
      return;
    }

    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (relatedTarget && addFormRef.current?.contains(relatedTarget)) {
      return;
    }

    window.requestAnimationFrame(() => {
      void handleAddItem();
    });
  };

  const handleOpenAddInput = (event: React.MouseEvent<HTMLElement>) => {
    setIsMultiAddMode(event.metaKey || event.ctrlKey);
    setShowAddInput(true);
  };

  const handleStartEdit = async (itemId: string, currentText: string) => {
    const item = items.find(i => i.id === itemId);
    setEditingItemId(itemId);
    setEditText(currentText);
    hideRecurringDayReadOnlyTooltip();
    setEditDateParts(parseUpcomingDateInput(date));
    setEditDateErrors(emptyUpcomingDateErrors());

    setAddTagsEnabled(false);
    setTagCategoriesInput('');
    setIsTagCategoriesFocused(false);
    setDueDateValue('');
    setIsHuge(false);
    setEstimatedTimeValue('');
    setEstimatedTimeUnit('minutes');
    setOpportunityCostIfNotDone('');
    setValueIfDone('');
    
    // Load recurring state if item has a recurringMetadataId
    if (item?.recurringMetadataId) {
      try {
        const recurringItem = await getRecurringItemAsync(item.recurringMetadataId);
        if (recurringItem) {
          setIsRecurring(true);
          setRecurrenceType(recurringItem.recurrenceType);
          if (recurringItem.recurrenceType === 'weekly') {
            setRecurringDays(recurringItem.weekdays || [getCurrentDayOfWeek()]);
            setMonthDays([1]);
          } else {
            setMonthDays(recurringItem.monthDays || [1]);
            setRecurringDays([getCurrentDayOfWeek()]);
          }
        } else {
          // Recurring item not found, treat as non-recurring
          setIsRecurring(false);
          setRecurrenceType('weekly');
          setRecurringDays([getCurrentDayOfWeek()]);
          setMonthDays([1]);
        }
      } catch (error) {
        console.error('Failed to load recurring metadata:', error);
        setIsRecurring(false);
        setRecurrenceType('weekly');
        setRecurringDays([getCurrentDayOfWeek()]);
        setMonthDays([1]);
      }
    } else {
      setIsRecurring(false);
      setRecurrenceType('weekly');
      setRecurringDays([getCurrentDayOfWeek()]);
      setMonthDays([1]);
    }
  };

  const handleTextFieldBlur = (e: React.FocusEvent, itemId: string) => {
    // Only save if focus is moving outside the edit form
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget && editFormRef.current?.contains(relatedTarget)) {
      // Focus is moving to another element within the form, don't save yet
      return;
    }
    // Focus is leaving the form, save the edit
    window.requestAnimationFrame(() => {
      void handleSaveEdit(itemId, { preserveEditorOnNoChange: true });
    });
  };

  const handleSaveEdit = async (itemId: string, options?: { preserveEditorOnNoChange?: boolean }) => {
    const item = items.find(i => i.id === itemId);
    if (!item) {
      setEditingItemId(null);
      setEditText('');
      return;
    }

    const normalizedEditText = editText.trim();
    if (!normalizedEditText) {
      if (options?.preserveEditorOnNoChange) {
        return;
      }
      setEditingItemId(null);
      setEditText('');
      return;
    }

    const changedText = normalizedEditText !== item.text.trim();
    const changedTags = false;
  const recurringStatusChanged = Boolean(item.recurringMetadataId) !== isRecurring;

    let targetDate: string | null = null;
    let changedDate = false;
    if (showDateField && currentEditingItem?.id === itemId && !item.recurringMetadataId) {
      const validationResult = validateUpcomingDateParts(editDateParts, currentYear, { allowPastYears: true });
      setEditDateErrors(validationResult.errors);
      if (validationResult.errors.day || validationResult.errors.month || validationResult.errors.year) {
        return;
      }

      const dateResult = buildUpcomingDateValue(validationResult.parts, { allowPastYears: true });
      if (!dateResult.date) {
        setEditDateErrors(prev => ({ ...prev, day: dateResult.error || prev.day }));
        return;
      }

      targetDate = dateResult.date;
      changedDate = targetDate !== date;
    }

    if (!changedText && !changedTags && !changedDate && !recurringStatusChanged) {
      if (options?.preserveEditorOnNoChange) {
        return;
      }
      setEditingItemId(null);
      setEditText('');
      setIsRecurring(false);
      setAddTagsEnabled(false);
      setTagCategoriesInput('');
      setIsTagCategoriesFocused(false);
      setDueDateValue('');
      setIsHuge(false);
      setEstimatedTimeValue('');
      setEstimatedTimeUnit('minutes');
      setOpportunityCostIfNotDone('');
      setValueIfDone('');
      setRecurrenceType('weekly');
      setRecurringDays([getCurrentDayOfWeek()]);
      setMonthDays([1]);
      setEditDateParts(parseUpcomingDateInput(date));
      setEditDateErrors(emptyUpcomingDateErrors());
      return;
    }
    
    try {
      // Handle recurring item changes
      if (isRecurring) {
        // Create or update recurring item
        const recurringData = {
          text: normalizedEditText,
          recurrenceType,
          weekdays: recurrenceType === 'weekly' ? recurringDays : undefined,
          monthDays: recurrenceType === 'monthly' ? monthDays : undefined,
          customColor: item.customColor,
        };
        
        if (item.recurringMetadataId) {
          // Update existing recurring item (updates template + all instances)
          await updateRecurringItem(item.recurringMetadataId, recurringData);
          // Notify parent to reload checklists
          onReloadAfterRecurringUpdate(itemId);
        } else {
          // Use the new atomic endpoint that creates recurring metadata and prevents duplicates
          const response = await datedChecklistService.makeItemRecurring(date, itemId, {
            text: normalizedEditText,
            recurrenceType,
            weekdays: recurrenceType === 'weekly' ? recurringDays : undefined,
            monthDays: recurrenceType === 'monthly' ? monthDays : undefined,
            customColor: item.customColor,
          });
          if (!response.success) {
            throw new Error(response.error || 'Failed to make item recurring');
          }
          // Notify parent to reload all checklists to show new recurring instances
          onReloadAfterRecurringUpdate(itemId);
        }
      } else if (item.recurringMetadataId) {
        // Was recurring, now isn't - convert to non-recurring (which deletes the pattern)
        // DO NOT call deleteRecurringItem first - that leaves orphaned data!
        onConvertToNonRecurring(itemId, normalizedEditText);
      } else if (changedDate && targetDate && onMoveItemToDate) {
        await onMoveItemToDate(itemId, targetDate, normalizedEditText);
      } else {
        // Non-recurring item - just update the text
        onEditItem(itemId, normalizedEditText);
      }
      
    } catch (error) {
      console.error('Failed to save recurring item:', error);
      // Still update the text even if recurring update failed
      onEditItem(itemId, normalizedEditText);
    }
    
    // Reset state
    setEditingItemId(null);
    setEditText('');
    setIsRecurring(false);
    setAddTagsEnabled(false);
    setTagCategoriesInput('');
    setIsTagCategoriesFocused(false);
    setDueDateValue('');
    setIsHuge(false);
    setEstimatedTimeValue('');
    setEstimatedTimeUnit('minutes');
    setOpportunityCostIfNotDone('');
    setValueIfDone('');
    setRecurrenceType('weekly');
    setRecurringDays([getCurrentDayOfWeek()]);
    setMonthDays([1]);
    setEditDateParts(parseUpcomingDateInput(date));
    setEditDateErrors(emptyUpcomingDateErrors());
    hideRecurringDayReadOnlyTooltip();
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditText('');
    setIsRecurring(false);
    setAddTagsEnabled(false);
    setTagCategoriesInput('');
    setIsTagCategoriesFocused(false);
    setDueDateValue('');
    setIsHuge(false);
    setEstimatedTimeValue('');
    setEstimatedTimeUnit('minutes');
    setOpportunityCostIfNotDone('');
    setValueIfDone('');
    setRecurrenceType('weekly');
    setRecurringDays([getCurrentDayOfWeek()]);
    setEditDateParts(parseUpcomingDateInput(date));
    setEditDateErrors(emptyUpcomingDateErrors());
    hideRecurringDayReadOnlyTooltip();
  };

  const handleEditKeyPress = (e: React.KeyboardEvent, itemId: string) => {
    if (e.key === 'Enter' && isTouchDevice) {
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit(itemId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  const handleItemClick = (e: React.MouseEvent, itemId: string, customColor: string | undefined) => {
    if (suppressNextItemClickRef.current) {
      suppressNextItemClickRef.current = false;
      return;
    }
    if (editingItemId === itemId || readOnly || isReorderMode) return;
    
    const target = e.target as HTMLElement;
    const isIconButton = target.closest('button');
    if (isIconButton) return;

    // Check if user is selecting text - if so, don't open color modal
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }

    setSelectedItemId(itemId);
    setSelectedItemColor(customColor);
    setColorModalAnchor(e.currentTarget as HTMLElement);
  };

  const closeItemContextMenus = () => {
    setContextMenuPosition(null);
    setContextMenuItemId(null);
    setMoveSubmenuAnchor(null);
    setTouchHoldItemId(null);
  };

  useEffect(() => {
    if (isReorderMode) {
      clearLongPressTimeout();
      closeItemContextMenus();
    }
  }, [isReorderMode]);

  useEffect(() => {
    if (!supportsReorderMode && isReorderMode) {
      setIsReorderMode(false);
    }
  }, [supportsReorderMode, isReorderMode]);

  const handleItemContextMenu = (event: React.MouseEvent<HTMLElement>, itemId: string) => {
    if (!isContextMenuEnabled) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setContextMenuItemId(itemId);
    setMoveSubmenuAnchor(null);
    setContextMenuPosition({ mouseX: event.clientX + 2, mouseY: event.clientY - 6 });
  };

  const clearLongPressTimeout = () => {
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
  };

  const handleItemTouchStart = (event: React.TouchEvent<HTMLElement>, itemId: string) => {
    if (!isContextMenuEnabled) {
      return;
    }
    setTouchHoldItemId(itemId);
    clearLongPressTimeout();
    const touch = event.touches[0];
    longPressTimeoutRef.current = setTimeout(() => {
      suppressNextItemClickRef.current = true;
      setTouchHoldItemId(null);
      setContextMenuItemId(itemId);
      setMoveSubmenuAnchor(null);
      setContextMenuPosition({ mouseX: touch.clientX, mouseY: touch.clientY });
    }, 500);
  };

  const handleItemTouchEnd = () => {
    clearLongPressTimeout();
    setTouchHoldItemId(null);
  };

  const handleToggleHiddenFromMenu = () => {
    if (!contextMenuItemId || !onToggleItemHidden) {
      closeItemContextMenus();
      return;
    }
    const item = items.find(entry => entry.id === contextMenuItemId);
    if (!item) {
      closeItemContextMenus();
      return;
    }
    const currentIsHidden = item.isHidden === true;
    const nextIsHidden = !currentIsHidden;

    const toggleResult = onToggleItemHidden(contextMenuItemId, nextIsHidden);
    if (toggleResult && typeof (toggleResult as Promise<void>).then === 'function') {
      void (toggleResult as Promise<void>).catch(() => {
        // Parent state is source of truth; no local override rollback needed.
      });
    }
    closeItemContextMenus();
  };

  const handleAddToPasoClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoveSubmenuAnchor(event.currentTarget);
  };

  const handleAddToPasoTarget = (targetType: ChecklistType) => {
    if (!contextMenuItemId || !onAddItemToPaso) {
      closeItemContextMenus();
      return;
    }
    void onAddItemToPaso(contextMenuItemId, targetType);
    closeItemContextMenus();
  };

  const handleCloseColorModal = () => {
    setColorModalAnchor(null);
    setSelectedItemId(null);
    setSelectedItemColor(undefined);
  };

  const handleColorSelect = (color: string) => {
    if (selectedItemId) {
      onColorChange(selectedItemId, color);
    }
    handleCloseColorModal();
  };

  const getItemBackgroundColor = (customColor: string | undefined): string => {
    if (customColor) {
      return customColor;
    }
    return 'background.paper';
  };

  const contextMenuItem = useMemo(
    () => (contextMenuItemId ? items.find(item => item.id === contextMenuItemId) : undefined),
    [contextMenuItemId, items]
  );
  const contextMenuItemIsHidden = contextMenuItem
    ? (contextMenuItem.isHidden === true)
    : false;
  const canAddContextItemToPaso = Boolean(contextMenuItem && !contextMenuItem.recurringMetadataId);

  type RenderEntry = {
    kind: 'group' | 'item';
    id: string;
    depth: number;
    groupedContext: boolean;
    isExpandableGroup?: boolean;
    expanded?: boolean;
    label?: string;
    itemCount?: number;
    item?: ChecklistItem;
  };

  const activeItems = useMemo(() => items.filter(item => !item.done), [items]);
  const hiddenActiveItems = useMemo(
    () => activeItems.filter(item => item.isHidden === true),
    [activeItems]
  );
  const hiddenActiveCount = hiddenActiveItems.length;
  const visibleActiveItems = useMemo(() => activeItems.filter(item => item.isHidden !== true), [activeItems]);
  const expandedHiddenItems = useMemo(() => (showHiddenItems ? hiddenActiveItems : []), [showHiddenItems, hiddenActiveItems]);
  const groupedModel = useMemo(() => buildGroupedTagModel(visibleActiveItems), [visibleActiveItems]);
  const flatGroupedEntries = useMemo(
    () => flattenGroupedTagModel(groupedModel, visibleActiveItems, expandedGroupIds),
    [groupedModel, visibleActiveItems, expandedGroupIds]
  );
  const hiddenGroupedModel = useMemo(() => buildGroupedTagModel(expandedHiddenItems), [expandedHiddenItems]);
  const flatHiddenGroupedEntries = useMemo(
    () => flattenGroupedTagModel(hiddenGroupedModel, expandedHiddenItems, expandedGroupIds),
    [hiddenGroupedModel, expandedHiddenItems, expandedGroupIds]
  );
  const renderEntries = useMemo<RenderEntry[]>(() => {
    return flatGroupedEntries.map((entry) => {
      if (entry.kind === 'group' && entry.group) {
        const expandedGroup = expandedGroupIds.has(entry.group.id);
        return {
          kind: 'group',
          id: entry.group.id,
          depth: entry.depth,
          groupedContext: false,
          isExpandableGroup: true,
          expanded: expandedGroup,
          label: entry.group.displayPath.replace(/:/g, ' > '),
          itemCount: entry.group.itemIds.length,
        };
      }

      return {
        kind: 'item',
        id: entry.item!.id,
        depth: entry.depth,
        groupedContext: entry.groupedContext,
        item: entry.item,
      };
    });
  }, [flatGroupedEntries, expandedGroupIds]);
  const hiddenRenderEntries = useMemo<RenderEntry[]>(() => {
    return flatHiddenGroupedEntries.map((entry) => {
      if (entry.kind === 'group' && entry.group) {
        const expandedGroup = expandedGroupIds.has(entry.group.id);
        return {
          kind: 'group',
          id: entry.group.id,
          depth: entry.depth,
          groupedContext: false,
          isExpandableGroup: true,
          expanded: expandedGroup,
          label: entry.group.displayPath.replace(/:/g, ' > '),
          itemCount: entry.group.itemIds.length,
        };
      }

      return {
        kind: 'item',
        id: entry.item!.id,
        depth: entry.depth,
        groupedContext: entry.groupedContext,
        item: entry.item,
      };
    });
  }, [flatHiddenGroupedEntries, expandedGroupIds]);

  const visualCount = showHiddenItems ? activeItems.length : visibleActiveItems.length;
  const showWarning = visibleActiveItems.length >= 10;
  const hiddenItemsToggleLabel = `${hiddenActiveCount} hidden ${hiddenActiveCount === 1 ? 'item' : 'items'}`;
  const hiddenItemsExpandLabel = `Show ${hiddenItemsToggleLabel}`;
  const hiddenItemsCollapseLabel = `Collapse ${hiddenItemsToggleLabel}`;
  const shouldShowHiddenToggleRow = isContextMenuEnabled && hiddenActiveCount > 0 && !isDragInProgress;

  useEffect(() => {
    if (hiddenActiveCount === 0 && showHiddenItems) {
      setShowHiddenItems(false);
    }
  }, [hiddenActiveCount, showHiddenItems]);

  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Helper to check if item is multi-line
  const isMultiLine = (text: string) => text.includes('\n');

  // Helper to get display text (first line + ellipsis for multi-line items)
  const getDisplayText = (text: string, itemId: string) => {
    if (!isMultiLine(text)) return text;
    if (expandedItemIds.has(itemId)) return text;
    const firstLine = text.split('\n')[0];
    return firstLine + '...';
  };

  const toggleItemExpansion = (itemId: string) => {
    setExpandedItemIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => onExpandChange(isExpanded)}
      sx={{ mb: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
          {onMarkReviewed !== undefined && (
            <Box
              role="button"
              tabIndex={0}
              data-testid={`review-button-${title}`}
              onClick={(e) => {
                e.stopPropagation();
                onMarkReviewed(!reviewed);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onMarkReviewed(!reviewed);
                }
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                borderRadius: '50%',
                color: reviewed ? 'success.main' : 'action.disabled',
                cursor: 'pointer',
                '&:hover': {
                  color: reviewed ? 'success.dark' : 'success.light',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <CheckIcon />
            </Box>
          )}
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {title}
            {/* Smooth transition between count and active items */}
            <Fade in={true} timeout={300}>
              <span style={{ transition: 'opacity 0.3s ease' }}>
                {(() => {
                  // If items are loaded, show active item count
                  if (items.length > 0) {
                    return visualCount > 0 ? `(${visualCount})` : '';
                  }
                  // If items not loaded yet but count is available, show count
                  if (itemCount !== undefined && itemCount > 0) {
                    return `(${itemCount})`;
                  }
                  // No count available
                  return '';
                })()}
              </span>
            </Fade>
            {dayOfWeek && (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
                <Box component="span" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                  {dayOfWeek}
                </Box>
              </Box>
            )}
            {supportsReorderMode && expanded && (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <IconButton
                  size="small"
                  data-testid="reorder-mode-control"
                  aria-label="Toggle reorder mode"
                  sx={{
                    ml: dayOfWeek ? 0 : 1,
                    color: isReorderMode ? AIRMASK_GREEN : 'text.secondary',
                    backgroundColor: isReorderMode ? 'action.hover' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    setIsReorderMode(prev => !prev);
                  }}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <DragIndicatorIcon fontSize="small" />
                </IconButton>
                {isReorderMode && (
                  <Typography component="span" variant="body2" sx={{ ml: 0.25, color: AIRMASK_GREEN, fontWeight: 600 }}>
                    Reorder
                  </Typography>
                )}
              </Box>
            )}
          </Typography>
        </Box>
      </AccordionSummary>
      {showWarning && (
        <AccordionDetails sx={{ pt: 2, pb: 0 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Too much on your plate - reprioritize, say no, or delegate!
          </Alert>
        </AccordionDetails>
      )}
      <AccordionDetails>
        <Box>
          {/* Show loading indicator when accordion expanded but items not loaded yet */}
          {isLoadingItems && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, justifyContent: 'center' }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading {itemCount} item{itemCount !== 1 ? 's' : ''}...
                </Typography>
              </Box>
              <Skeleton variant="rounded" height={38} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={38} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={38} />
            </Box>
          )}

          <Droppable droppableId={date}>
            {(provided, snapshot) => (
              <List
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  minHeight: 50,
                  backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                  borderRadius: 1,
                  p: 0,
                }}
              >
                {(() => {
                  let draggableIndex = 0;
                  const entries: React.ReactNode[] = [];

                  const renderEntryCollection = (entryCollection: RenderEntry[], sectionPrefix: string, depthOffset = 0) => {
                    entryCollection.forEach((entry) => {
                    if (entry.kind === 'group') {
                      const indentPx = (entry.depth + depthOffset) * 20;
                      entries.push(
                        <ListItem
                          key={`group-${sectionPrefix}-${entry.id}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 0.5,
                            ml: `${indentPx}px`,
                            width: `calc(100% - ${indentPx}px)`,
                            boxSizing: 'border-box',
                            p: 0.75,
                            borderLeft: '2px solid #32CA1D',
                            borderRadius: 1,
                            backgroundColor: 'action.hover',
                          }}
                        >
                          {entry.isExpandableGroup ? (
                            <IconButton size="small" onClick={() => toggleGroupExpansion(entry.id)}>
                              {entry.expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          ) : (
                            <Box sx={{ width: 32, height: 32 }} />
                          )}
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            {entry.label}
                          </Typography>
                          <Chip
                            label={`${entry.itemCount} paso${entry.itemCount === 1 ? '' : 's'}`}
                            size="small"
                            variant="outlined"
                          />
                        </ListItem>
                      );
                      return;
                    }

                    const item = entry.item!;
                    const currentIndex = draggableIndex;
                    const indentPx = (entry.depth + depthOffset) * 20;
                    const isTouchHoldActive = touchHoldItemId === item.id;
                    draggableIndex += 1;

                    if (editingItemId === item.id) {
                      return;
                    }

                    entries.push(
                      <Draggable key={item.id} draggableId={item.id} index={currentIndex} isDragDisabled={!isDraggingEnabled}>
                        {(provided, snapshot) => (
                          <ListItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...(isDraggingEnabled ? provided.dragHandleProps : {})}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 0.5,
                              ml: `${indentPx}px`,
                              width: `calc(100% - ${indentPx}px)`,
                              boxSizing: 'border-box',
                              p: 1,
                              backgroundColor: isTouchHoldActive
                                ? getItemBackgroundColor(item.customColor)
                                : (snapshot.isDragging ? 'action.selected' : getItemBackgroundColor(item.customColor)),
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              cursor: editingItemId === item.id || readOnly ? 'default' : 'pointer',
                              borderLeft: isMultiLine(item.text) ? '3px solid #32CA1D' : '1px solid',
                              borderLeftColor: isMultiLine(item.text) ? '#32CA1D' : 'divider',
                              WebkitTapHighlightColor: 'transparent',
                              '&:active': {
                                backgroundColor: getItemBackgroundColor(item.customColor),
                              },
                              '&:hover': readOnly
                                ? {}
                                : item.customColor
                                  ? { opacity: editingItemId === item.id ? 1 : 0.8 }
                                  : {
                                      backgroundColor: editingItemId === item.id ? 'background.paper' : 'action.hover',
                                    },
                            }}
                            onClick={(e) => handleItemClick(e, item.id, item.customColor)}
                            onContextMenu={(event) => handleItemContextMenu(event, item.id)}
                            onTouchStart={(event) => handleItemTouchStart(event, item.id)}
                            onTouchEnd={handleItemTouchEnd}
                            onTouchCancel={handleItemTouchEnd}
                          >
                        {isMultiLine(item.text) && editingItemId !== item.id && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleItemExpansion(item.id);
                            }}
                            sx={{ p: 0.5 }}
                          >
                            {expandedItemIds.has(item.id) ? (
                              <KeyboardArrowUpIcon 
                                fontSize="small" 
                                sx={{ flexShrink: 0 }} 
                              />
                            ) : (
                              <KeyboardArrowDownIcon 
                                fontSize="small" 
                                sx={{ flexShrink: 0 }} 
                              />
                            )}
                          </IconButton>
                        )}
                        {editingItemId === item.id ? (
                          <Box ref={editFormRef} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                              <TextField
                                fullWidth
                                size="small"
                                multiline
                                minRows={isMultiLine(item.text) ? 2 : 1}
                                maxRows={10}
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                onKeyDown={(e) => handleEditKeyPress(e, item.id)}
                                onBlur={(e) => handleTextFieldBlur(e, item.id)}
                                autoFocus
                                sx={{ flexGrow: 1 }}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="Shift+Enter for new line, Enter to save"
                              />
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSaveEdit(item.id);
                                }}
                                color="primary"
                                disabled={!editText.trim()}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelEdit();
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <Box 
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={isRecurring}
                                    onChange={(e) => setIsRecurring(e.target.checked)}
                                    size="small"
                                  />
                                }
                                label={
                                  <Typography variant="body2">
                                    Recurring?
                                  </Typography>
                                }
                              />

                              {supportsTagEditing && (
                                <>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={addTagsEnabled}
                                        onChange={(e) => setAddTagsEnabled(e.target.checked)}
                                        size="small"
                                      />
                                    }
                                    label={<Typography variant="body2">Add tags?</Typography>}
                                  />

                                  {addTagsEnabled && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                                  <TextField
                                    size="small"
                                    label="Categories"
                                    InputLabelProps={{ shrink: true }}
                                    placeholder={isTagCategoriesFocused ? 'Fruits, Fruits:Berries' : undefined}
                                    value={tagCategoriesInput}
                                    onChange={(e) => setTagCategoriesInput(e.target.value)}
                                    onFocus={() => setIsTagCategoriesFocused(true)}
                                    onBlur={() => setIsTagCategoriesFocused(false)}
                                    multiline
                                    minRows={1}
                                    fullWidth
                                  />
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                      size="small"
                                      label="Est time"
                                      InputLabelProps={{ shrink: true }}
                                      type={isHuge ? 'text' : 'number'}
                                      value={isHuge ? '—' : estimatedTimeValue}
                                      onChange={(e) => setEstimatedTimeValue(e.target.value)}
                                      disabled={isHuge}
                                      sx={{
                                        flex: 1,
                                        '& input[type=number]': {
                                          MozAppearance: 'textfield',
                                        },
                                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                          WebkitAppearance: 'none',
                                          margin: 0,
                                        },
                                      }}
                                    />
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                      <InputLabel id={`date-time-unit-${item.id}`} shrink>Unit</InputLabel>
                                      <Select
                                        labelId={`date-time-unit-${item.id}`}
                                        value={estimatedTimeUnit}
                                        displayEmpty
                                        label="Unit"
                                        disabled={isHuge}
                                        renderValue={(selected) => selected ? selected : <NullSelectSymbol />}
                                        onChange={(e: SelectChangeEvent) => setEstimatedTimeUnit(e.target.value as '' | 'minutes' | 'hours')}
                                      >
                                        <MenuItem value="">
                                          <NullSelectSymbol />
                                        </MenuItem>
                                        <MenuItem value="minutes">Minutes</MenuItem>
                                        <MenuItem value="hours">Hours</MenuItem>
                                      </Select>
                                    </FormControl>
                                    <FormControlLabel
                                      sx={{
                                        ml: 0,
                                        mr: 0,
                                        height: 40,
                                        minHeight: 40,
                                        alignSelf: 'flex-end',
                                        '& .MuiFormControlLabel-label': {
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                        },
                                      }}
                                      control={
                                        <Checkbox
                                          size="small"
                                          sx={{
                                            color: AIRMASK_GREEN,
                                            '&.Mui-checked': {
                                              color: AIRMASK_GREEN,
                                            },
                                          }}
                                          checked={isHuge}
                                          onChange={(e) => {
                                            const nextIsHuge = e.target.checked;
                                            setIsHuge(nextIsHuge);
                                            if (nextIsHuge) {
                                              setEstimatedTimeValue('');
                                              setEstimatedTimeUnit('');
                                              return;
                                            }
                                            if (estimatedTimeUnit === '') {
                                              setEstimatedTimeUnit('minutes');
                                            }
                                          }}
                                        />
                                      }
                                      label={
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                          <Typography variant="body2">Is Huge?</Typography>
                                          <Tooltip title="Too large to estimate">
                                            <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                          </Tooltip>
                                        </Box>
                                      }
                                    />
                                  </Box>
                                  <TextField
                                    size="small"
                                    label="Do by"
                                    InputLabelProps={{ shrink: true }}
                                    type="date"
                                    value={dueDateValue}
                                    onChange={(e) => setDueDateValue(e.target.value)}
                                    onMouseDown={(e) => {
                                      const input = e.target as HTMLInputElement & { showPicker?: () => void };
                                      if (!input.showPicker) return;
                                      try {
                                        input.showPicker();
                                      } catch {
                                        // Some browsers reject showPicker despite pointer gestures.
                                      }
                                    }}
                                    fullWidth
                                    sx={{
                                      '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                        display: 'none',
                                        WebkitAppearance: 'none',
                                      },
                                      '& input[type="date"]::-webkit-datetime-edit': {
                                        color: dueDateValue ? 'inherit' : 'transparent',
                                      },
                                      '& input[type="date"]:focus::-webkit-datetime-edit': {
                                        color: 'inherit',
                                      },
                                    }}
                                  />
                                  <FormControl size="small" fullWidth>
                                    <InputLabel
                                      id={`date-cost-${item.id}`}
                                      shrink
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        pointerEvents: 'auto',
                                        color: 'text.secondary',
                                        backgroundColor: 'background.paper',
                                        pr: 0.5,
                                      }}
                                    >
                                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'inherit' }}>
                                        Neglect cost
                                        <Tooltip title="Cost if not done">
                                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                            <InfoOutlinedIcon sx={{ fontSize: 15, color: 'inherit' }} />
                                          </Box>
                                        </Tooltip>
                                      </Box>
                                    </InputLabel>
                                    <Select
                                      labelId={`date-cost-${item.id}`}
                                      value={opportunityCostIfNotDone}
                                      displayEmpty
                                      label="Neglect cost"
                                      renderValue={(selected) => selected
                                        ? <PrioritySelectValue value={selected as PriorityLevel} reserveDropdownArrowSpace />
                                        : <NullSelectSymbol />}
                                      onChange={(e: SelectChangeEvent) => setOpportunityCostIfNotDone(e.target.value as '' | 'high' | 'moderate' | 'low')}
                                      aria-label="Neglect cost"
                                    >
                                      <MenuItem value="">
                                        <NullSelectSymbol />
                                      </MenuItem>
                                      <MenuItem value="high">
                                        <PriorityMenuOption value="high" />
                                      </MenuItem>
                                      <MenuItem value="moderate">
                                        <PriorityMenuOption value="moderate" />
                                      </MenuItem>
                                      <MenuItem value="low">
                                        <PriorityMenuOption value="low" />
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel id={`date-value-${item.id}`} shrink>Value if done</InputLabel>
                                    <Select
                                      labelId={`date-value-${item.id}`}
                                      value={valueIfDone}
                                      displayEmpty
                                      label="Value if done"
                                      renderValue={(selected) => selected
                                        ? <PrioritySelectValue value={selected as PriorityLevel} reserveDropdownArrowSpace />
                                        : <NullSelectSymbol />}
                                      onChange={(e: SelectChangeEvent) => setValueIfDone(e.target.value as '' | 'high' | 'moderate' | 'low')}
                                      aria-label="Value if done"
                                    >
                                      <MenuItem value="">
                                        <NullSelectSymbol />
                                      </MenuItem>
                                      <MenuItem value="high">
                                        <PriorityMenuOption value="high" />
                                      </MenuItem>
                                      <MenuItem value="moderate">
                                        <PriorityMenuOption value="moderate" />
                                      </MenuItem>
                                      <MenuItem value="low">
                                        <PriorityMenuOption value="low" />
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </Box>
                                  )}
                                </>
                              )}

                              <Divider sx={{ my: 1 }} />
                              {isRecurring && (
                                <>
                                  <RecurrenceTypeSelector
                                    recurrenceType={recurrenceType}
                                    onChange={setRecurrenceType}
                                  />
                                  {recurrenceType === 'weekly' ? (
                                    <WeekdaySelector
                                      selectedDays={recurringDays}
                                      onChange={setRecurringDays}
                                      currentDay={getCurrentDayOfWeek()}
                                    />
                                  ) : (
                                    <MonthDayInput
                                      selectedDays={monthDays}
                                      onChange={setMonthDays}
                                    />
                                  )}
                                </>
                              )}
                            </Box>
                          </Box>
                        ) : (
                          <>
                            <MarkdownText 
                              sx={{ 
                                pl: 1.5, 
                                userSelect: readOnly ? 'text' : 'none',
                                cursor: readOnly ? 'text' : 'pointer'
                              }}
                              onCopy={readOnly ? (e: React.ClipboardEvent) => {
                                e.preventDefault();
                                e.clipboardData.setData('text/plain', item.text);
                              } : undefined}
                            >
                              {getDisplayText(item.text, item.id)}
                            </MarkdownText>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {checklistType === 'escalated' && (
                                <Tooltip title="Escalated 📈">
                                  <Chip size="small" label="📈" />
                                </Tooltip>
                              )}
                              {(!entry.groupedContext ? (item.tags?.categories || []).slice(0, 5) : []).map(category => (
                                <Chip key={`${item.id}-${category}`} size="small" label={category.replace(/:/g, ' > ')} variant="outlined" />
                              ))}
                              {item.tags?.special?.estimatedTimeToDoneMinutes !== undefined && (
                                <Chip size="small" label={<TimeChipLabel minutes={item.tags.special.estimatedTimeToDoneMinutes} />} />
                              )}
                              {item.tags?.special?.dueDate && (
                                <Tooltip title="Paso-Upcoming linkage to arrive at future date">
                                  <Chip size="small" label={<DueDateChipLabel dueDate={item.tags.special.dueDate} />} />
                                </Tooltip>
                              )}
                              {item.tags?.special?.opportunityCostIfNotDone && (
                                <Chip size="small" label={<PriorityChipLabel kind="cost" value={item.tags.special.opportunityCostIfNotDone} />} />
                              )}
                              {item.tags?.special?.valueIfDone && (
                                <Chip size="small" label={<PriorityChipLabel kind="value" value={item.tags.special.valueIfDone} />} />
                              )}
                            </Box>
                            {item.recurringMetadataId && (
                              <RecurringItemBadge recurringMetadataId={item.recurringMetadataId} />
                            )}
                            {!readOnly && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartEdit(item.id, item.text);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <Tooltip
                                  title={item.recurringMetadataId ? "Convert to non-recurring to delete" : ""}
                                  arrow
                                  disableInteractive
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={!!item.recurringMetadataId}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteItem(item.id);
                                      }}
                                      sx={{
                                        '&.Mui-disabled': {
                                          opacity: 0.4,
                                        },
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </>
                            )}
                          </>
                        )}
                          </ListItem>
                        )}
                      </Draggable>
                    );
                    });
                  };

                  renderEntryCollection(renderEntries, 'visible', 0);

                  if (shouldShowHiddenToggleRow) {
                    entries.push(
                      <ListItem
                        key="hidden-toggle-row"
                        onClick={() => setShowHiddenItems(prev => !prev)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 0.75,
                          p: 0.75,
                          border: '1px dashed',
                          borderColor: 'divider',
                          borderRadius: 1,
                          cursor: 'pointer',
                          backgroundColor: 'action.hover',
                          '&:hover': {
                            backgroundColor: 'action.selected',
                          },
                        }}
                      >
                        {showHiddenItems ? (
                          <VisibilityOffIcon fontSize="small" color="action" />
                        ) : (
                          <VisibilityIcon fontSize="small" color="action" />
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {showHiddenItems ? hiddenItemsCollapseLabel : hiddenItemsExpandLabel}
                        </Typography>
                      </ListItem>
                    );

                    if (showHiddenItems && hiddenRenderEntries.length > 0) {
                      renderEntryCollection(hiddenRenderEntries, 'hidden', 1);
                    }
                  }

                  return entries;
                })()}
                {provided.placeholder}
              </List>
            )}
          </Droppable>

          {!readOnly && (
            <>
              {showAddInput ? (
                <Box ref={addFormRef} sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={10}
                    placeholder={
                      isMultiAddMode
                        ? 'Enter items... (Empty lines separate items)'
                        : 'Enter item text... (Shift+Enter for new line)'
                    }
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                    onKeyDown={handleKeyPress}
                    onBlur={handleAddTextFieldBlur}
                    autoFocus
                  />
                  <IconButton color="primary" onClick={() => { void handleAddItem(); }} disabled={!newItemText.trim()}>
                    <AddIcon />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <IconButton
                    color="primary"
                    onMouseEnter={handleAddButtonHover}
                    onMouseMove={handleAddButtonHover}
                    onMouseLeave={() => setShowMultiAddHint(false)}
                    onClick={handleOpenAddInput}
                    sx={{
                      border: '1px dashed',
                      borderColor: 'primary.main',
                      borderRadius: 1,
                      width: '100%',
                    }}
                  >
                    <AddIcon />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {showMultiAddHint ? 'Add Multiple Items' : 'Add Item'}
                    </Typography>
                  </IconButton>
                </Box>
              )}
            </>
          )}
        </Box>
      </AccordionDetails>

      <ColorSwatchModal
        open={Boolean(colorModalAnchor)}
        anchorEl={colorModalAnchor}
        onClose={handleCloseColorModal}
        onColorSelect={handleColorSelect}
        currentColor={selectedItemColor}
      />

      <ContextMenu
        position={contextMenuPosition}
        onClose={closeItemContextMenus}
      >
        <MenuItem onClick={handleToggleHiddenFromMenu}>
          <ListItemIcon>
            {contextMenuItemIsHidden ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{contextMenuItemIsHidden ? 'Unhide' : 'Hide'}</ListItemText>
        </MenuItem>
        {canAddContextItemToPaso && (
          <MenuItem onClick={handleAddToPasoClick}>
            <ListItemIcon>
              <MoveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add to PASOS</ListItemText>
            <ChevronRightIcon fontSize="small" color="action" />
          </MenuItem>
        )}
      </ContextMenu>

      <Menu
        open={Boolean(moveSubmenuAnchor)}
        anchorEl={moveSubmenuAnchor}
        onClose={() => setMoveSubmenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {(pasoChecklistConfigs || []).map(config => (
          <MenuItem key={config.type} onClick={() => handleAddToPasoTarget(config.type)}>
            {config.title}
          </MenuItem>
        ))}
      </Menu>

      <GenericModal
        open={Boolean(editingItemId)}
        onClose={handleCancelEdit}
        ariaLabelledBy="date-checklist-edit-item-modal"
      >
        <Box ref={editFormRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography id="date-checklist-edit-item-modal" variant="h6" sx={{ fontWeight: 600 }}>
            Edit item
          </Typography>

          {currentEditingItem && (
            <>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={isMultiLine(currentEditingItem.text) ? 2 : 1}
                maxRows={10}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => handleEditKeyPress(e, currentEditingItem.id)}
                onBlur={(e) => handleTextFieldBlur(e, currentEditingItem.id)}
                autoFocus
                sx={{ flexGrow: 1 }}
                onClick={(e) => e.stopPropagation()}
                placeholder="Shift+Enter for new line, Enter to save"
              />

              {showDateField && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Day
                  </Typography>
                  <Tooltip
                    title="Day is read-only for recurring items"
                    open={showRecurringDayReadOnlyTooltip && !!currentEditingItem?.recurringMetadataId}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    onClose={hideRecurringDayReadOnlyTooltip}
                    arrow
                  >
                    <Box sx={{ width: 'fit-content' }}>
                      <UpcomingDateEntryField
                        day={editDateParts.day}
                        month={editDateParts.month}
                        year={editDateParts.year}
                        errors={editDateErrors}
                        onDayChange={(event) => {
                          const nextValue = event.target.value;
                          const validationResult = validateUpcomingDateParts({ ...editDateParts, day: nextValue }, currentYear, { allowPastYears: true });
                          setEditDateParts(validationResult.parts);
                          setEditDateErrors(validationResult.errors);
                        }}
                        onMonthChange={(event) => {
                          const nextValue = event.target.value.toUpperCase();
                          const validationResult = validateUpcomingDateParts({ ...editDateParts, month: nextValue }, currentYear, { allowPastYears: true });
                          setEditDateParts(validationResult.parts);
                          setEditDateErrors(validationResult.errors);
                        }}
                        onYearChange={(event) => {
                          const nextValue = event.target.value;
                          const validationResult = validateUpcomingDateParts({ ...editDateParts, year: nextValue }, currentYear, { allowPastYears: true });
                          setEditDateParts(validationResult.parts);
                          setEditDateErrors(validationResult.errors);
                        }}
                        onDayInteract={handleRecurringDayFieldInteraction}
                        onMonthInteract={handleRecurringDayFieldInteraction}
                        onYearInteract={handleRecurringDayFieldInteraction}
                        readOnly={!!currentEditingItem?.recurringMetadataId}
                        compact
                      />
                    </Box>
                  </Tooltip>
                </Box>
              )}

              <Box
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={isRecurring}
                      onChange={(e) => setIsRecurring(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Recurring?
                    </Typography>
                  }
                />

                {supportsTagEditing && (
                  <>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={addTagsEnabled}
                          onChange={(e) => setAddTagsEnabled(e.target.checked)}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Add tags?</Typography>}
                    />

                    {addTagsEnabled && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                    <TextField
                      size="small"
                      label="Categories"
                      InputLabelProps={{ shrink: true }}
                      placeholder={isTagCategoriesFocused ? 'Fruits, Fruits:Berries' : undefined}
                      value={tagCategoriesInput}
                      onChange={(e) => setTagCategoriesInput(e.target.value)}
                      onFocus={() => setIsTagCategoriesFocused(true)}
                      onBlur={() => setIsTagCategoriesFocused(false)}
                      multiline
                      minRows={1}
                      fullWidth
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small"
                        label="Est time"
                        InputLabelProps={{ shrink: true }}
                        type={isHuge ? 'text' : 'number'}
                        value={isHuge ? '—' : estimatedTimeValue}
                        onChange={(e) => setEstimatedTimeValue(e.target.value)}
                        disabled={isHuge}
                        sx={{
                          flex: 1,
                          '& input[type=number]': {
                            MozAppearance: 'textfield',
                          },
                          '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                        }}
                      />
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id={`date-time-unit-${currentEditingItem.id}`} shrink>Unit</InputLabel>
                        <Select
                          labelId={`date-time-unit-${currentEditingItem.id}`}
                          value={estimatedTimeUnit}
                          displayEmpty
                          label="Unit"
                          disabled={isHuge}
                          renderValue={(selected) => selected ? selected : <NullSelectSymbol />}
                          onChange={(e: SelectChangeEvent) => setEstimatedTimeUnit(e.target.value as '' | 'minutes' | 'hours')}
                        >
                          <MenuItem value="">
                            <NullSelectSymbol />
                          </MenuItem>
                          <MenuItem value="minutes">Minutes</MenuItem>
                          <MenuItem value="hours">Hours</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        sx={{
                          ml: 0,
                          mr: 0,
                          height: 40,
                          minHeight: 40,
                          alignSelf: 'flex-end',
                          '& .MuiFormControlLabel-label': {
                            display: 'inline-flex',
                            alignItems: 'center',
                          },
                        }}
                        control={
                          <Checkbox
                            size="small"
                            sx={{
                              color: AIRMASK_GREEN,
                              '&.Mui-checked': {
                                color: AIRMASK_GREEN,
                              },
                            }}
                            checked={isHuge}
                            onChange={(e) => {
                              const nextIsHuge = e.target.checked;
                              setIsHuge(nextIsHuge);
                              if (nextIsHuge) {
                                setEstimatedTimeValue('');
                                setEstimatedTimeUnit('');
                                return;
                              }
                              if (estimatedTimeUnit === '') {
                                setEstimatedTimeUnit('minutes');
                              }
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">Is Huge?</Typography>
                            <Tooltip title="Too large to estimate">
                              <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                            </Tooltip>
                          </Box>
                        }
                      />
                    </Box>
                    <TextField
                      size="small"
                      label="Do by"
                      InputLabelProps={{ shrink: true }}
                      type="date"
                      value={dueDateValue}
                      onChange={(e) => setDueDateValue(e.target.value)}
                      onMouseDown={(e) => {
                        const input = e.target as HTMLInputElement & { showPicker?: () => void };
                        if (!input.showPicker) return;
                        try {
                          input.showPicker();
                        } catch {
                          // Some browsers reject showPicker despite pointer gestures.
                        }
                      }}
                      fullWidth
                      sx={{
                        '& input[type="date"]::-webkit-calendar-picker-indicator': {
                          display: 'none',
                          WebkitAppearance: 'none',
                        },
                        '& input[type="date"]::-webkit-datetime-edit': {
                          color: dueDateValue ? 'inherit' : 'transparent',
                        },
                        '& input[type="date"]:focus::-webkit-datetime-edit': {
                          color: 'inherit',
                        },
                      }}
                    />
                    <FormControl size="small" fullWidth>
                      <InputLabel
                        id={`date-cost-${currentEditingItem.id}`}
                        shrink
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          pointerEvents: 'auto',
                          color: 'text.secondary',
                          backgroundColor: 'background.paper',
                          pr: 0.5,
                        }}
                      >
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'inherit' }}>
                          Neglect cost
                          <Tooltip title="Cost if not done">
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                              <InfoOutlinedIcon sx={{ fontSize: 15, color: 'inherit' }} />
                            </Box>
                          </Tooltip>
                        </Box>
                      </InputLabel>
                      <Select
                        labelId={`date-cost-${currentEditingItem.id}`}
                        value={opportunityCostIfNotDone}
                        displayEmpty
                        label="Neglect cost"
                        renderValue={(selected) => selected
                          ? <PrioritySelectValue value={selected as PriorityLevel} reserveDropdownArrowSpace />
                          : <NullSelectSymbol />}
                        onChange={(e: SelectChangeEvent) => setOpportunityCostIfNotDone(e.target.value as '' | 'high' | 'moderate' | 'low')}
                        aria-label="Neglect cost"
                      >
                        <MenuItem value="">
                          <NullSelectSymbol />
                        </MenuItem>
                        <MenuItem value="high">
                          <PriorityMenuOption value="high" />
                        </MenuItem>
                        <MenuItem value="moderate">
                          <PriorityMenuOption value="moderate" />
                        </MenuItem>
                        <MenuItem value="low">
                          <PriorityMenuOption value="low" />
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth>
                      <InputLabel id={`date-value-${currentEditingItem.id}`} shrink>Value if done</InputLabel>
                      <Select
                        labelId={`date-value-${currentEditingItem.id}`}
                        value={valueIfDone}
                        displayEmpty
                        label="Value if done"
                        renderValue={(selected) => selected
                          ? <PrioritySelectValue value={selected as PriorityLevel} reserveDropdownArrowSpace />
                          : <NullSelectSymbol />}
                        onChange={(e: SelectChangeEvent) => setValueIfDone(e.target.value as '' | 'high' | 'moderate' | 'low')}
                        aria-label="Value if done"
                      >
                        <MenuItem value="">
                          <NullSelectSymbol />
                        </MenuItem>
                        <MenuItem value="high">
                          <PriorityMenuOption value="high" />
                        </MenuItem>
                        <MenuItem value="moderate">
                          <PriorityMenuOption value="moderate" />
                        </MenuItem>
                        <MenuItem value="low">
                          <PriorityMenuOption value="low" />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                    )}
                  </>
                )}

                <Divider sx={{ my: 1 }} />
                {isRecurring && (
                  <>
                    <RecurrenceTypeSelector
                      recurrenceType={recurrenceType}
                      onChange={setRecurrenceType}
                    />
                    {recurrenceType === 'weekly' ? (
                      <WeekdaySelector
                        selectedDays={recurringDays}
                        onChange={setRecurringDays}
                        currentDay={getCurrentDayOfWeek()}
                      />
                    ) : (
                      <MonthDayInput
                        selectedDays={monthDays}
                        onChange={setMonthDays}
                      />
                    )}
                  </>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <SaveActionButton
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleSaveEdit(currentEditingItem.id);
                  }}
                  disabled={!editText.trim()}
                  text={showDateField ? undefined : 'SAVE'}
                  ariaLabel="Save"
                  size="small"
                />
              </Box>
            </>
          )}
        </Box>
      </GenericModal>
    </Accordion>
  );
};

export default DateChecklistExpand;
